import React from "react";

/**
 * Shared inline-SVG icon set. No external icon dependency.
 * Every icon accepts standard SVG props (className, etc.).
 */
const base = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  viewBox: "0 0 24 24",
};

/* Jira issue-type icons, drawn to match the team's Jira issue-type menu.
 * Each carries its OWN Jira colour (not currentColor), so a type looks the
 * same on every screen, and every one renders at 20px: any size class a
 * caller passes (h-*, w-*, size-*) is dropped so they can never drift apart.
 * `muted` greys an icon out for empty states. */
const SIZE_CLASS = /(^|\s)(?:h|w|size|min-h|min-w|max-h|max-w)-\S+/g;
function jiraIcon(children) {
  return function JiraIcon({ className = "", muted = false, ...rest }) {
    const cls = [
      "h-5 w-5 flex-none",
      String(className).replace(SIZE_CLASS, " ").trim(),
      muted ? "opacity-40 grayscale" : "",
    ]
      .filter(Boolean)
      .join(" ");
    return (
      <svg
        viewBox="0 0 24 24"
        width="20"
        height="20"
        aria-hidden={rest["aria-label"] ? undefined : "true"}
        {...rest}
        className={cls}
      >
        {children}
      </svg>
    );
  };
}

export const Icons = {
  Spark: (p) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...p}>
      <path d="M12 2C12 2 14.5 8.5 16 10c1.5 1.5 6 2 6 2s-4.5.5-6 2c-1.5 1.5-4 8-4 8s-2.5-6.5-4-8c-1.5-1.5-6-2-6-2s4.5-.5 6-2C9.5 8.5 12 2 12 2z" />
    </svg>
  ),
  /* Jira issue types (outline glyphs, as Jira draws them). */
  JiraEpic: jiraIcon(
    <path d="M13.2 2.8 5.6 13.4h5.6l-1.4 7.8 7.6-10.6h-5.6l1.4-7.8Z" fill="none" stroke="#A35BE8" strokeWidth="1.9" strokeLinejoin="round" />
  ),
  /* Story: a slightly wide rectangle with a tick — Task keeps the square. */
  JiraStory: jiraIcon(
    <>
      <rect x="2.5" y="5" width="19" height="14" rx="3" fill="none" stroke="#357DE8" strokeWidth="1.9" />
      <path d="m8.2 12.2 2.6 2.6 5-5" fill="none" stroke="#357DE8" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  JiraTask: jiraIcon(
    <>
      <rect x="3.5" y="3.5" width="17" height="17" rx="3.5" fill="none" stroke="#357DE8" strokeWidth="1.9" />
      <path d="m8 12.3 2.8 2.8L16.2 9.4" fill="none" stroke="#357DE8" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  JiraBug: jiraIcon(
    <g fill="none" stroke="#E5493A" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.2 7.2a2.8 2.8 0 0 1 5.6 0" />
      <rect x="7.6" y="7.6" width="8.8" height="12.4" rx="4.4" />
      <path d="M12 9.6v9.6" />
      <path d="M7.6 11H4.5M7.6 14.5H4M7.6 18 5 19.8M16.4 11h3.1M16.4 14.5H20M16.4 18l2.6 1.8M9.5 5.2 8 3.6M14.5 5.2 16 3.6" />
    </g>
  ),
  /* Xray issue types (filled squares, as Xray draws them). */
  JiraXrayTest: jiraIcon(
    <>
      <rect x="2.5" y="2.5" width="19" height="19" rx="4" fill="#117885" />
      <circle cx="12" cy="12" r="4.6" fill="none" stroke="#fff" strokeWidth="2.6" />
    </>
  ),
  JiraTestSet: jiraIcon(
    <>
      <rect x="2.5" y="2.5" width="19" height="19" rx="4" fill="#5E4DB2" />
      <circle cx="10" cy="14" r="3.6" fill="#fff" fillOpacity=".62" />
      <circle cx="14" cy="10" r="3.6" fill="#fff" />
    </>
  ),
  JiraTestPlan: jiraIcon(
    <>
      <rect x="2.5" y="2.5" width="19" height="19" rx="4" fill="#E06C00" />
      <rect x="7.2" y="7" width="9.6" height="2.2" rx="1.1" fill="#fff" />
      <rect x="7.2" y="10.9" width="9.6" height="2.2" rx="1.1" fill="#fff" />
      <rect x="7.2" y="14.8" width="6" height="2.2" rx="1.1" fill="#fff" />
    </>
  ),
  JiraTestExecution: jiraIcon(
    <>
      <rect x="2.5" y="2.5" width="19" height="19" rx="4" fill="#82B536" />
      <path d="M9.4 7.6v8.8a.6.6 0 0 0 .9.5l7-4.4a.6.6 0 0 0 0-1l-7-4.4a.6.6 0 0 0-.9.5Z" fill="#fff" />
    </>
  ),
  JiraPrecondition: jiraIcon(
    <>
      <rect x="2.5" y="2.5" width="19" height="19" rx="4" fill="#1D9AAA" />
      <circle cx="12" cy="12" r="5" fill="none" stroke="#fff" strokeWidth="2.2" />
      <circle cx="13" cy="11" r="1.9" fill="#fff" />
    </>
  ),
  Boards: (p) => (
    <svg {...base} {...p}>
      <rect x="3" y="3" width="7" height="18" rx="1.5" />
      <rect x="14" y="3" width="7" height="11" rx="1.5" />
    </svg>
  ),
  Grid: (p) => (
    <svg {...base} {...p}>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </svg>
  ),
  List: (p) => (
    <svg {...base} {...p}>
      <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
    </svg>
  ),
  Play: (p) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...p}>
      <path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l11-6.86a1 1 0 0 0 0-1.72l-11-6.86A1 1 0 0 0 8 5.14z" />
    </svg>
  ),
  Pause: (p) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...p}>
      <rect x="6" y="4" width="4" height="16" rx="1" />
      <rect x="14" y="4" width="4" height="16" rx="1" />
    </svg>
  ),
  Refresh: (p) => (
    <svg {...base} {...p}>
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <path d="M21 3v6h-6" />
    </svg>
  ),
  /* Two arrows round a cycle: made again (an App Page Test or Artifact re-created). */
  Recreate: (p) => (
    <svg {...base} {...p}>
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
      <path d="M8 16H3v5" />
    </svg>
  ),
  Inbox: (p) => (
    <svg {...base} {...p}>
      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </svg>
  ),
  Settings: (p) => (
    <svg {...base} {...p}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
  /* Configuration: a gear holding three sliders (Settings › Configuration). */
  Configuration: (p) => (
    <svg {...base} {...p}>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      <circle cx="12" cy="12" r="4.6" />
      <path d="M9.6 10.4h4.8M9.6 12h4.8M9.6 13.6h4.8" strokeWidth="1.1" />
      <circle cx="12.9" cy="10.4" r="0.75" fill="currentColor" stroke="none" />
      <circle cx="10.8" cy="12" r="0.75" fill="currentColor" stroke="none" />
      <circle cx="13.4" cy="13.6" r="0.75" fill="currentColor" stroke="none" />
    </svg>
  ),
  Eye: (p) => (
    <svg {...base} {...p}>
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  Plus: (p) => (
    <svg {...base} {...p}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  ),
  /* A chat bubble: send something to the assistant's chat. */
  Chat: (p) => (
    <svg {...base} {...p}>
      <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20.5l1.4-4.6A8 8 0 1 1 21 12z" />
    </svg>
  ),
  ChevronDown: (p) => (
    <svg {...base} {...p}>
      <path d="M6 9l6 6 6-6" />
    </svg>
  ),
  ChevronUp: (p) => (
    <svg {...base} {...p}>
      <path d="M18 15l-6-6-6 6" />
    </svg>
  ),
  ChevronRight: (p) => (
    <svg {...base} {...p}>
      <path d="M9 18l6-6-6-6" />
    </svg>
  ),
  ChevronLeft: (p) => (
    <svg {...base} {...p}>
      <path d="M15 18l-6-6 6-6" />
    </svg>
  ),
  ArrowLeft: (p) => (
    <svg {...base} {...p}>
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  ),
  // Puts a value into a field (Choose a value for {NAME}).
  ArrowRight: (p) => (
    <svg {...base} {...p}>
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  ),
  SortAsc: (p) => (
    <svg {...base} {...p}>
      <path d="M3 6h13M3 11h10M3 16h7M19 8v8M16 11l3-3 3 3" />
    </svg>
  ),
  SortDesc: (p) => (
    <svg {...base} {...p}>
      <path d="M3 6h13M3 11h10M3 16h7M19 8v8M16 13l3 3 3-3" />
    </svg>
  ),
  Check: (p) => (
    <svg {...base} {...p}>
      <path d="M20 6L9 17l-5-5" />
    </svg>
  ),
  X: (p) => (
    <svg {...base} {...p}>
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  ),
  Clock: (p) => (
    <svg {...base} {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  ),
  Calendar: (p) => (
    <svg {...base} {...p}>
      <rect x="3" y="4" width="18" height="17" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  ),
  Minus: (p) => (
    <svg {...base} {...p}>
      <path d="M5 12h14" />
    </svg>
  ),
  Search: (p) => (
    <svg {...base} {...p}>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.35-4.35" />
    </svg>
  ),
  Spinner: (p) => (
    <svg viewBox="0 0 24 24" fill="none" {...p}>
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z" />
    </svg>
  ),
  Sun: (p) => (
    <svg {...base} {...p}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  ),
  Moon: (p) => (
    <svg {...base} {...p}>
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  ),
  LogOut: (p) => (
    <svg {...base} {...p}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="M16 17l5-5-5-5M21 12H9" />
    </svg>
  ),
  Trash: (p) => (
    <svg {...base} {...p}>
      <path d="M3 6h18" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  ),
  Camera: (p) => (
    <svg {...base} {...p}>
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  ),
  ShieldCheck: (p) => (
    <svg {...base} {...p}>
      <path d="M12 2l8 3v6c0 5-3.5 9-8 11-4.5-2-8-6-8-11V5l8-3z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  ),
  Star: (p) => (
    <svg {...base} {...p}>
      <path d="M12 2l3 6.5 7 1-5 4.9 1.2 7L12 18l-6.2 3.4 1.2-7-5-4.9 7-1L12 2z" />
    </svg>
  ),
  // Certification-badge with a check — 4-lobe silhouette so it stays legible
  // at 16px. Reads instantly as "validated". Used for Run validations.
  BadgeCheck: (p) => (
    <svg {...base} {...p}>
      <path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  ),
  Pencil: (p) => (
    <svg {...base} {...p}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  ),
  Users: (p) => (
    <svg {...base} {...p}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  /* A person in a circle — Jira's "For you" icon. */
  UserCircle: (p) => (
    <svg {...base} {...p}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="10" r="3" />
      <path d="M6.2 18.4a6.5 6.5 0 0 1 11.6 0" />
    </svg>
  ),
  Home: (p) => (
    <svg {...base} {...p}>
      <path d="M3 10.5L12 3l9 7.5" />
      <path d="M5 9.5V21h5v-6h4v6h5V9.5" />
    </svg>
  ),
  Chart: (p) => (
    <svg {...base} {...p}>
      <path d="M3 3v18h18" />
      <path d="M7 15v-4M12 15V7M17 15v-6" />
    </svg>
  ),
  Report: (p) => (
    <svg {...base} {...p}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M8 13h8M8 17h5" />
    </svg>
  ),
  /* Rolled scroll — used by Build Playwright scripts to signify "the test
     script we're about to generate". A classic parchment: a rolled edge at
     the top-left + flat body + three text lines inside. */
  Script: (p) => (
    <svg {...base} {...p}>
      <path d="M8 3h11a1 1 0 0 1 1 1v3a2 2 0 0 1-2 2H8" />
      <path d="M8 3a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V9" />
      <path d="M10 13h7M10 16h5" />
    </svg>
  ),
  /* Lucide "variable" — "(x)": a scenario's {VARIABLE} typed in at Generate. */
  Variable: (p) => (
    <svg {...base} {...p}>
      <path d="M8 21s-4-3-4-9 4-9 4-9" />
      <path d="M16 3s4 3 4 9-4 9-4 9" />
      <path d="M15 9l-6 6" />
      <path d="M9 9l6 6" />
    </svg>
  ),
  /* Lucide "eye-off" — hide a typed-in secret again. */
  EyeOff: (p) => (
    <svg {...base} {...p}>
      <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.53 13.53 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
      <path d="M2 2l20 20" />
    </svg>
  ),
  /* Lucide "scroll-text" — an Artifact (the generated Playwright script):
     the Artifacts menu item and the Artifacts column. */
  ScrollText: (p) => (
    <svg {...base} {...p}>
      <path d="M15 12h-5" />
      <path d="M15 8h-5" />
      <path d="M19 17V5a2 2 0 0 0-2-2H4" />
      <path d="M8 21h12a2 2 0 0 0 2-2v-1a1 1 0 0 0-1-1H11a1 1 0 0 0-1 1v1a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v2a1 1 0 0 0 1 1h3" />
    </svg>
  ),
  Download: (p) => (
    <svg {...base} {...p}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="M7 10l5 5 5-5" />
      <path d="M12 15V3" />
    </svg>
  ),
  Activity: (p) => (
    <svg {...base} {...p}>
      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
    </svg>
  ),
  /* The activity log: who did what, and when. */
  Log: (p) => (
    <svg {...base} {...p}>
      <path d="M13 6h8M13 12h8M13 18h8M3 6h1M3 12h1M3 18h1M8 6h1M8 12h1M8 18h1" />
    </svg>
  ),
  Maximize: (p) => (
    <svg {...base} {...p}>
      <path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3" />
    </svg>
  ),
  /* Maximize's corners turned inwards: back to the smaller size. */
  Minimize: (p) => (
    <svg {...base} {...p}>
      <path d="M8 3v3a2 2 0 0 1-2 2H3M21 8h-3a2 2 0 0 1-2-2V3M3 16h3a2 2 0 0 1 2 2v3M16 21v-3a2 2 0 0 1 2-2h3" />
    </svg>
  ),
  More: (p) => (
    <svg {...base} {...p}>
      <circle cx="12" cy="5" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="12" cy="19" r="1.6" />
    </svg>
  ),
  Unlink: (p) => (
    <svg {...base} {...p}>
      <path d="M9 17H7A5 5 0 0 1 7 7h2" />
      <path d="M15 7h2a5 5 0 0 1 4 8" />
      <path d="M8 12h3" />
      <path d="M2 2l20 20" />
    </svg>
  ),
  // Warning triangle — used where the user is about to repeat work that already
  // exists (duplicate test creation, drifted Xray steps).
  Alert: (p) => (
    <svg {...base} {...p}>
      <path d="M10.3 3.9L2.5 17.5A2 2 0 0 0 4.2 20.5h15.6a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </svg>
  ),
  // Two stacked sheets — "a copy already exists".
  Copy: (p) => (
    <svg {...base} {...p}>
      <rect x="9" y="9" width="12" height="12" rx="2" />
      <path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" />
    </svg>
  ),
  // Question mark in a circle — opens the per-screen help dialog.
  Help: (p) => (
    <svg {...base} {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.2 9.2a2.9 2.9 0 0 1 5.6 1c0 1.9-2.8 2.2-2.8 4" />
      <path d="M12 17.5h.01" />
    </svg>
  ),
  // Open book — the help dialog's own heading mark.
  Book: (p) => (
    <svg {...base} {...p}>
      <path d="M12 6.5C12 6.5 9.5 4.5 4 4.5v13c5.5 0 8 2 8 2s2.5-2 8-2v-13c-5.5 0-8 2-8 2z" />
      <path d="M12 6.5v15" />
    </svg>
  ),
  // Funnel — filter the board by epic / story card.
  Filter: (p) => (
    <svg {...base} {...p}>
      <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />
    </svg>
  ),
  // Chain link — a story card's linked Xray / Playwright tests.
  Link: (p) => (
    <svg {...base} {...p}>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  ),
  // Layered requirement board — the Requirement Board nav/section mark.
  Layers: (p) => (
    <svg {...base} {...p}>
      <path d="M12 2L2 7l10 5 10-5-10-5z" />
      <path d="M2 17l10 5 10-5" />
      <path d="M2 12l10 5 10-5" />
    </svg>
  ),
  // Padlock — a disabled/locked action that needs a prior selection.
  Lock: (p) => (
    <svg {...base} {...p}>
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  ),
};

export default Icons;
